import { useState } from "react";
import { Field, FieldColSize } from "./Field";
import { Button, Checkbox, Flex, Form, Input, InputNumber, Popconfirm, Radio, theme } from "antd";
import { generateRandomString } from "../../../shared/utils";
import { DeleteOutlined, RightCircleOutlined } from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";

export interface RadioGroupOption {
    value: string;
    label?: string;
}

const DEFAULT_FIELD_COL_SIZE: FieldColSize = {
    xs: 24,
    sm: 12,
    md: 8,
    lg: 8,
    xl: 8,
};

const normalizeColSize = (colSize?: Partial<FieldColSize>): FieldColSize => {
    return {
        ...DEFAULT_FIELD_COL_SIZE,
        ...(colSize ?? {}),
    };
};

const RadioGroupFieldRenderer: React.FC<{ field: RadioGroupField }> = ({ field }) => {
    const { token } = theme.useToken();
    const options = (field.options ?? []).map((o) => ({
        value: o.value,
        label: o.label ?? o.value,
    }));

    return (
        <div className={field.hidden ? "hidden" : undefined}>
            <Form.Item
                label={
                    <Flex gap={8}>
                        <RightCircleOutlined style={{ color: token.colorPrimary }} />
                        {field.label}
                    </Flex>
                }
                rules={[{ required: field.required }]}
                name={field.name}
                initialValue={field.defaultValue}
            >
                <Radio.Group options={options}
                    optionType="button"
                    buttonStyle="solid" />
            </Form.Item>
        </div>
    );
};

export class RadioGroupField implements Field {
    id = generateRandomString(10);
    name = "RadioGroupField" + generateRandomString(4);
    description = "A radio group with configurable options";
    label = "Radio group";
    required = false;
    defaultValue: string | undefined = undefined;
    hidden = false;
    colSize: FieldColSize = DEFAULT_FIELD_COL_SIZE;
    formTag = "RadioGroupField";
    parent: Field | null = null;
    options: RadioGroupOption[] = [];

    constructor(isTemplate = false) {
        if (isTemplate) {
            this.name = "Radio group";
        }
    }

    render = () => {
        return <RadioGroupFieldRenderer field={this} />;
    };

    renderEditor = (
        field: Field,
        selectedComponent: unknown,
        setSelectedComponent: unknown,
        deleteComponent: (c: Field) => void
    ) => {
        return (
            <RadioGroupFieldEditor
                field={field}
                deleteComponent={deleteComponent}
            />
        );
    };

    renderPropertiesEditor = (field: Field, updateField: (f: Field) => void) => {
        return (
            <RadioGroupFieldPropsEditor
                inputField={field}
                setInputField={updateField}
            />
        );
    };

    static fromJSON = (json: any, parent: Field | null = null): RadioGroupField => {
        const radio = new RadioGroupField();
        radio.id = json.id;
        radio.label = json.label;
        radio.name = json.name;
        radio.description = json.description;
        radio.required = json.required;
        radio.defaultValue = json.defaultValue;
        radio.hidden = json.hidden ?? false;
        radio.colSize = normalizeColSize(json.colSize);
        radio.formTag = json.formTag;
        radio.parent = parent;
        radio.options = Array.isArray(json.options)
            ? json.options.map((o: any) => ({
                value: o.value ?? "",
                label: o.label,
            }))
            : [];
        return radio;
    };

    toJSON = () => {
        return {
            id: this.id,
            name: this.name,
            description: this.description,
            label: this.label,
            required: this.required,
            defaultValue: this.defaultValue,
            hidden: this.hidden,
            colSize: this.colSize,
            formTag: this.formTag,
            parent: this.parent?.id,
            options: this.options,
        };
    };
}

export const RadioGroupFieldEditor = ({
    field,
    deleteComponent,
}: {
    field: Field;
    deleteComponent: (c: Field) => void;
}) => {
    const t = useTranslate();
    return (
        <Flex justify="space-between">
            <Form.Item
                label={
                    <span>
                        {field.label}
                        {field.hidden && (
                            <span className="ml-2 text-gray-400 text-xs">
                                ({t("forms.fieldHidden")})
                            </span>
                        )}
                    </span>
                }
                name={field.name}
                className="w-full"
                rules={[{ required: field.required }]}
            >
                <span className="text-gray-500">{t("forms.fieldRadioGroup")}</span>
            </Form.Item>
            <Popconfirm
                title="Delete the item"
                description="Are you sure to delete this item?"
                onConfirm={() => deleteComponent(field)}
                okText="Yes"
                cancelText="No"
            >
                <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
        </Flex>
    );
};

export const RadioGroupFieldPropsEditor = ({
    inputField,
    setInputField,
}: {
    inputField: Field;
    setInputField: (f: Field) => void;
}) => {
    const t = useTranslate();
    const [required, setRequired] = useState(inputField.required);
    const [hidden, setHidden] = useState(inputField.hidden ?? false);
    const field = inputField as RadioGroupField;
    const options = field.options ?? [];
    const colSize = normalizeColSize(field.colSize);

    const handleInputChange = (prop: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        (inputField as any)[prop] = e.target.value;
        setInputField(inputField);
    };

    const handleCheckboxChange = (prop: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        if (prop === "required") setRequired(checked);
        if (prop === "hidden") setHidden(checked);
        (inputField as any)[prop] = checked;
        setInputField(inputField);
    };

    const addOption = () => {
        field.options = [...options, { value: "", label: "" }];
        setInputField(inputField);
    };

    const updateOption = (index: number, key: "value" | "label", value: string) => {
        const next = [...options];
        next[index] = { ...next[index], [key]: value };
        field.options = next;
        setInputField(inputField);
    };

    const removeOption = (index: number) => {
        field.options = options.filter((_, i) => i !== index);
        setInputField(inputField);
    };

    const handleColSizeChange = (key: keyof FieldColSize) => (value: number | null) => {
        const nextValue = Math.min(24, Math.max(1, Number(value ?? DEFAULT_FIELD_COL_SIZE[key])));
        field.colSize = {
            ...normalizeColSize(field.colSize),
            [key]: nextValue,
        };
        setInputField(inputField);
    };

    return (
        <Form layout="vertical">
            <Form.Item label="Name">
                <Input
                    value={inputField.name}
                    onChange={handleInputChange("name")}
                />
            </Form.Item>
            <Form.Item label="Description">
                <Input
                    value={inputField.description}
                    onChange={handleInputChange("description")}
                />
            </Form.Item>
            <Form.Item label="Label">
                <Input
                    value={inputField.label}
                    onChange={handleInputChange("label")}
                />
            </Form.Item>
            <Form.Item label="Required">
                <Checkbox
                    checked={required}
                    onChange={handleCheckboxChange("required")}
                />
            </Form.Item>
            <Form.Item label={t("forms.fieldHidden")}>
                <Checkbox
                    checked={hidden}
                    onChange={handleCheckboxChange("hidden")}
                />
            </Form.Item>
            <Form.Item label="Default Value">
                <Input
                    value={inputField.defaultValue ?? ""}
                    onChange={handleInputChange("defaultValue")}
                />
            </Form.Item>
            <Form.Item label={t("forms.columnSize")}>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{t("forms.columnSizeXs")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.xs} onChange={handleColSizeChange("xs")} />
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{t("forms.columnSizeSm")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.sm} onChange={handleColSizeChange("sm")} />
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{t("forms.columnSizeMd")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.md} onChange={handleColSizeChange("md")} />
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{t("forms.columnSizeLg")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.lg} onChange={handleColSizeChange("lg")} />
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{t("forms.columnSizeXl")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.xl} onChange={handleColSizeChange("xl")} />
                    </div>
                </div>
            </Form.Item>
            <Form.Item label={t("forms.addOption")}>
                <Button type="dashed" onClick={addOption} className="w-full">
                    {t("forms.addOption")}
                </Button>
            </Form.Item>
            {options.length > 0 && (
                <Form.Item label={t("forms.options")}>
                    <div className="flex flex-col gap-2">
                        {options.map((opt, index) => (
                            <Flex key={index} gap={8} align="center">
                                <Input
                                    placeholder={t("forms.optionValue")}
                                    value={opt.value}
                                    onChange={(e) =>
                                        updateOption(index, "value", e.target.value)
                                    }
                                    className="flex-1"
                                />
                                <Input
                                    placeholder={t("forms.optionLabel")}
                                    value={opt.label ?? ""}
                                    onChange={(e) =>
                                        updateOption(index, "label", e.target.value)
                                    }
                                    className="flex-1"
                                />
                                <Popconfirm
                                    title="Delete this option?"
                                    onConfirm={() => removeOption(index)}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                    />
                                </Popconfirm>
                            </Flex>
                        ))}
                    </div>
                </Form.Item>
            )}
        </Form>
    );
};
